import React from 'react';
import { ChevronRight } from 'lucide-react';
import { formatRelativeTime, formatDuration } from 'shared/utils/dateUtils';
import { AVAILABLE_COLUMNS, DEFAULT_COLUMNS } from 'features/activity/config/userColumns';
import {
  TABLE_STYLES,
  tableRowClass,
  tableDataCellClass,
} from 'features/activity/styles/dataTableStyles';

const UserRow = ({ user, onRowClick, columns, styles: stylesProp }) => {
  const styles = stylesProp || TABLE_STYLES;

  const getCellValue = (columnKey) => {
    const def = AVAILABLE_COLUMNS[columnKey];
    const field = def?.field || columnKey;

    switch (field) {
      case 'distinctId': {
        const raw = user.distinctId || user.userId;
        if (!raw) return '—';
        const s = String(raw);
        // Match agent-runs DistinctIdLink: head-truncate at 32 chars + ellipsis.
        return s.length > 32 ? `${s.slice(0, 32)}…` : s;
      }
      case 'firstVisit':
        if (!user.firstVisit) return 'N/A';
        return formatRelativeTime(new Date(user.firstVisit));
      case 'lastVisitTime':
      case 'lastSession':
        if (!user.lastVisitTime && !user.lastSession) return 'N/A';
        return formatRelativeTime(new Date(user.lastVisitTime || user.lastSession));
      case 'totalPageViews':
        return user.totalPageViews != null ? String(user.totalPageViews) : 'N/A';
      case 'isWeb3User':
        return user.isWeb3User ? 'Yes' : 'No';
      case 'isBounce':
        return user.isBounce ? 'Yes' : 'No';
      case 'firstLocationCity':
      case 'lastLocationCity':
      case 'firstLocationCountry':
      case 'lastLocationCountry':
      case 'firstReferrer':
      case 'lastReferrer':
      case 'deviceType':
      case 'browserName':
      case 'os': {
        const val = user[field];
        return val != null && val !== '' ? String(val) : '—';
      }
      case 'primaryWalletAddress': {
        const raw = user.primaryWalletAddress;
        if (!raw || raw === '') return '—';
        const s = String(raw);
        return s.length > 32 ? `${s.slice(0, 32)}…` : s;
      }
      case 'totalTimeSpent':
        if (user.totalTimeSpent == null) return 'N/A';
        return formatDuration(user.totalTimeSpent);
      // Agent activity (from agent_runs, attached by the list endpoint).
      case 'lastActiveAt':
        return user.lastActiveAt ? formatRelativeTime(new Date(user.lastActiveAt)) : '—';
      case 'agentRuns':
        return user.agentRuns != null ? Number(user.agentRuns).toLocaleString() : '—';
      case 'agentErrors':
        return user.agentErrors != null ? Number(user.agentErrors).toLocaleString() : '—';
      case 'agentCost': {
        const c = Number(user.agentCost);
        if (!Number.isFinite(c)) return '—';
        return c === 0 ? '$0' : c < 0.01 ? '<$0.01' : `$${c.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
      case 'agents':
        return Array.isArray(user.agents) && user.agents.length ? user.agents.join(', ') : '—';
      case 'networthUsd':
        if (user.networthUsd == null) return 'N/A';
        return `$${user.networthUsd.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`;
      default:
        return user[field] ?? '—';
    }
  };

  const displayColumns = columns && columns.length > 0 ? columns : DEFAULT_COLUMNS.order;
  const userId = user.distinctId || user.userId;

  return (
    <tr
      onClick={() => onRowClick && onRowClick(user)}
      className={tableRowClass}
    >
      {displayColumns.map((columnKey) => {
        const value = getCellValue(columnKey);
        const def = AVAILABLE_COLUMNS[columnKey];
        const field = def?.field || columnKey;
        // A zero count is a value, not a gap.
        const isMissing = value == null || value === '' || value === '—' || value === 'N/A';
        const alignRight = def?.align === 'right';
        const isErrorCount = field === 'agentErrors' && Number(user.agentErrors) > 0;
        // Distinct ID and wallet both render as identifier text — mono, primary
        // text color (NOT the blue link color — distinctId should read as a
        // table value, not a hyperlink).
        const isLinkId = field === 'distinctId';
        const isMonoPrimary = field === 'primaryWalletAddress';
        let cellClass = 'text-[12px]';
        let cellColor;
        if (isMissing) {
          cellClass = 'text-[12px] text-[color:var(--text-muted)]';
        } else if (isLinkId) {
          cellClass = 'font-mono text-[11px] text-[color:var(--text-primary)] underline-offset-2 hover:underline';
        } else if (isMonoPrimary) {
          cellClass = 'font-mono text-[11px] text-[color:var(--text-primary)]';
        } else if (isErrorCount) {
          cellClass = 'text-[12px] text-[color:var(--feedback-error)]';
        } else {
          cellColor = styles.textWarmSecondary || 'var(--text-secondary)';
        }
        return (
          <td key={columnKey} className={`${tableDataCellClass} whitespace-nowrap ${alignRight ? 'text-right tabular-nums' : ''}`}>
            <div className={`${cellClass}${field === 'agents' ? ' max-w-[220px] truncate' : ''}`} style={cellColor ? { color: cellColor } : undefined}>
              <span title={field === 'lastActiveAt' && user.lastActiveAt ? new Date(user.lastActiveAt).toLocaleString() : typeof value === 'string' ? value : ''}>{isMissing ? '—' : value}</span>
            </div>
          </td>
        );
      })}
      <td className={`${tableDataCellClass} w-8 text-right`}>
        <ChevronRight className="h-4 w-4 inline" style={{ color: styles.textSecondary }} />
      </td>
    </tr>
  );
};

export default UserRow;
